/*
 * Segmented control: two to four mutually exclusive views or modes, with a sliding thumb (as the landing's
 * monthly/yearly toggle). Use it for the billing cycle, time ranges and view switches.
 *   <Segmented value={cycle} onValueChange={setCycle} aria-label={t('cycle.label')}
 *     options={[{ value: 'monthly', label: t('cycle.monthly') }, { value: 'yearly', label: t('cycle.yearly') }]} />
 */
import { motion, useReducedMotion } from 'motion/react'
import { ToggleGroup } from 'radix-ui'
import { useId, type ReactNode } from 'react'
import { cn } from '@/lib/cn'

export function Segmented<V extends string>({
  value,
  onValueChange,
  options,
  size = 'md',
  className,
  'aria-label': ariaLabel,
}: {
  value: V
  onValueChange: (v: V) => void
  options: { value: V; label: ReactNode; badge?: ReactNode }[]
  size?: 'sm' | 'md'
  className?: string
  'aria-label': string
}) {
  const reduce = useReducedMotion()
  const layoutId = useId()
  return (
    <ToggleGroup.Root
      type="single"
      value={value}
      onValueChange={(v) => v && onValueChange(v as V)}
      aria-label={ariaLabel}
      className={cn(
        'inline-flex items-center gap-0.5 rounded-full bg-control p-1 ring-1 ring-hairline ring-inset',
        className,
      )}
    >
      {options.map((o) => {
        const active = o.value === value
        return (
          <ToggleGroup.Item
            key={o.value}
            value={o.value}
            className={cn(
              'relative inline-flex items-center gap-1.5 rounded-full font-semibold transition-colors duration-150 touch:min-h-11',
              size === 'sm' ? 'h-7 px-3 text-12' : 'h-8 px-4 text-13',
              active ? 'text-ink' : 'text-muted hover:text-ink',
            )}
          >
            {active ? (
              <motion.span
                layoutId={layoutId}
                className="absolute inset-0 rounded-full bg-surface shadow-sm ring-1 ring-hairline"
                transition={reduce ? { duration: 0 } : { type: 'spring', stiffness: 460, damping: 36 }}
              />
            ) : null}
            <span className="relative">{o.label}</span>
            {o.badge ? <span className="relative">{o.badge}</span> : null}
          </ToggleGroup.Item>
        )
      })}
    </ToggleGroup.Root>
  )
}
